import type { OcclusionOptions } from ".";
import { AddNodes, AddNodesMin, AttrChange, AttrChangeMin, CSSDeleteRule, CSSDeleteRuleMin, CSSInsertRule, CSSInsertRuleMin, ChangeType, CharacterDataChange, CharacterDataChangeMin, ClientIp, ClientIpMin, DOMChanges, DOMChangesMin, DeviceData, DeviceDataMin, DomReady, DomReadyMin, FocusChange, FocusChangeMin, INode, INodeMin, InputChange, InputChangeMin, MouseClickChange, MouseClickChangeMin, MouseMoveChange, MouseMoveChangeMin, MouseOutChange, MouseOutChangeMin, MouseOverChange, MouseOverChangeMin, PageLoad, PageLoadMin, RemoveNodes, RemoveNodesMin, ScrollChange, ScrollChangeMin, SessionEvent, SessionEventMin, URLChange, URLChangeMin, UserProperty, UserPropertyMin, WindowResize, WindowResizeMin, INodeMeta, IMGMeta, IMGMetaMin, VideoMeta, VideoMetaMin, MediaInfoMin, MediaInfo, MouseDoubleClickChange, MouseDoubleClickChangeMin, ViewPortResize, ViewPortResizeMin, TouchChangeMin, TouchChange, Gesture, GestureMin, GestureType, CheckedChange, CheckedChangeMin } from "../../../types";
import { SDKNumVersion, SDKVersion } from "./const";
import { IsSwipe, SwipeDirection } from "./utils";

interface ICollect {
	onChange: ( onChange: DOMChanges | DOMChangesMin ) => void;
	onSeqUpdate: ( seq: number ) => void;
	onNewStartTime: ( startTime: number ) => void;
	startTime: number | null, // if it's the first load, it will be null
	seq: number;
	min: boolean;
	sendLog: ( log: string ) => void;
	sessionCounter: number;
	abort: () => void;
	appKey: string;
}

const processCSSRule = ( rule: string ) => {
	const useRule = ( rule || '' )
	return useRule
}

interface IRemoveNodeMutation {
	parent: Node
	node: Node
	prevSibling: Node | null
	nextSibling: Node | null
}

const getOS = () => {
	let osName = 'Unknown OS'
	if (navigator.userAgent.indexOf("Win") != -1) osName = "Windows";
	if (navigator.userAgent.indexOf("Mac") != -1) osName = "Mac OS";
	if (navigator.userAgent.indexOf("Linux") != -1) osName = "Linux";
	if (navigator.userAgent.indexOf("Android") != -1) osName = "Android";
	if (navigator.userAgent.indexOf("like Mac") != -1) osName = "iOS";
	return osName
}

export const collectUTM = () => {
	const { search } = location
	const params = new URLSearchParams( search )

	const utmData: Record<string, string> = {}

	// iterate over params starting with utm_
	for ( const [ key, value ] of params.entries() ) {
		if ( key.startsWith( 'utm_' ) ) {
			utmData[key] = value
		}
	}

	return utmData
}


interface SequencedMutationRecord extends MutationRecord {
	seq: number
	time: number
}

const getBrowser = () => {
	const browser = 'Unknown Browser'
	const userAgent = navigator.userAgent

	// YaBrowser
	if ( userAgent.indexOf( 'YaBrowser' ) !== -1 ) {
		return 'Yandex'
	}
	if ( userAgent.indexOf( 'SamsungBrowser' ) !== -1 ) {
		return 'Samsung'
	}
	// Edge
	if ( userAgent.indexOf( 'Edge' ) !== -1 ) {
		return 'Edge'
	}
	if ( userAgent.indexOf( 'Edg/' ) !== -1 ) {
		return 'Edge'
	}
	// IE
	if ( userAgent.indexOf( 'MSIE' ) !== -1 ) {
		return 'IE'
	}
	// Opera
	if ( userAgent.indexOf( 'OPR' ) !== -1 ) {
		return 'Opera'
	}
	// Chrome
	if ( userAgent.indexOf( 'Chrome' ) !== -1 ) {
		return 'Chrome'
	}
	// CriOS
	if ( userAgent.indexOf( 'CriOS' ) !== -1 ) {
		return 'Chrome'
	}
	// Firefox
	if ( userAgent.indexOf( 'Firefox' ) !== -1 ) {
		return 'Firefox'
	}
	// Safari
	if ( userAgent.indexOf( 'Safari' ) !== -1 ) {
		return 'Safari'
	}
	return browser
}

const getBrowserVersion = () => {
	const browserVersion = 'Unknown Version'
	const userAgent = navigator.userAgent
	if ( userAgent.indexOf( 'YaBrowser' ) !== -1 ) {
		const match = userAgent.match( /YaBrowser\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'SamsungBrowser' ) !== -1 ) {
		const match = userAgent.match( /SamsungBrowser\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'Edge' ) !== -1 ) {
		const match = userAgent.match( /Edge\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'Edg/' ) !== -1 ) {
		const match = userAgent.match( /Edg\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'MSIE' ) !== -1 ) {
		const match = userAgent.match( /MSIE ([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'OPR' ) !== -1 ) {
		const match = userAgent.match( /OPR\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'Chrome' ) !== -1 ) {
		const match = userAgent.match( /Chrome\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'CriOS' ) !== -1 ) {
		const match = userAgent.match( /CriOS\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'Firefox' ) !== -1 ) {
		const match = userAgent.match( /Firefox\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'Safari' ) !== -1 ) {
		const match = userAgent.match( /Version\/([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	return browserVersion
}

const getOSVersion = () => {
	const osVersion = 'unknown'
	const userAgent = navigator.userAgent
	if ( userAgent.indexOf( 'Windows' ) !== -1 ) {
		// win11
		if ( /(Windows 11.0)/.test( userAgent ) ) return '11'
		// win10
		if ( /(Windows 10.0|Windows NT 10.0)/.test( userAgent ) ) return '10'
		// win8.1
		if ( /(Windows 8.1|Windows NT 6.3)/.test( userAgent ) ) return '8.1'
		// win8
		if ( /(Windows 8|Windows NT 6.2)/.test( userAgent ) ) return '8'
		// win7
		if ( /(Windows 7|Windows NT 6.1)/.test( userAgent ) ) return '7'
		// winVista
		if ( /(Windows Vista|Windows NT 6.0)/.test( userAgent ) ) return 'Vista'
		// winServer2003
		if ( /(Windows Server 2003)/.test( userAgent ) ) return 'Server 2003'
		// winXP
		if ( /(Windows NT 5.1|Windows XP)/.test( userAgent ) ) return 'XP'
	}
	// iOS
	if ( userAgent.indexOf( 'iPhone' ) !== -1 || userAgent.indexOf( 'iPad' ) !== -1 ) {
		// Extract the iOS version
		const match = userAgent.match( /OS ([0-9_]*) like Mac OS X/ )
		if ( match ) {
			return match[1].replace( '_', '.' )
		}
	}
	if ( userAgent.indexOf( 'Mac' ) !== -1 ) {
		// Extract the OS X version
		// eslint-disable-next-line no-useless-escape
		const match = userAgent.match( /Mac OS X (10[\.\_\d]+)/ )
		if ( match ) {
			return match[1].replace( '_', '.' )
		}
	}
	if ( userAgent.indexOf( 'Android' ) !== -1 ) {
		const match = userAgent.match( /Android\s([0-9.]*)/ )
		if ( match ) {
			return match[1]
		}
	}
	if ( userAgent.indexOf( 'like Mac' ) !== -1 ) {
		const match = userAgent.match( /CPU OS ([0-9_]*) like Mac OS X/ )
		if ( match ) {
			return match[1].replace( '_', '.' )
		}
	}
	if ( userAgent.indexOf( 'Linux' ) !== -1 ) {
		// Extract the Linux version
		const match = userAgent.match( /Linux\s([0-9.]*)/ )
		if ( match ) {
			return match[1] || 'unknown'
		}
	}

	return osVersion
}

const getDeviceType = () => {
	const userAgent = navigator.userAgent
	if ( /(Tablet|IPad)/.test( userAgent ) ) {
		// check for explicit iPad
		if ( /iPad/.test( userAgent ) ) {
			return 'Tablet'
		}
		return 'Tablet'
	}
	if ( /Mobile|IPhone/.test( userAgent ) ) {
		// check for explicit iPhone
		if ( /iPhone/.test( userAgent ) ) {
			return 'Phone'
		}
		return 'Phone'
	}
	
	// cheeck agains isTouchDevice and resolution
	if ( isTouchDevice() ) {
		// check for tablet resolution
		if ( window.screen.availWidth > 1024 ) {
			return 'Tablet'
		}
		return 'Phone'
	}

	return 'Desktop'
}

const isTouchDevice = () => {
	return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

const deviceManufacturer = () => {
	const userAgent = navigator.userAgent
	if ( /Android/.test( userAgent ) ) {
		return 'unknown'
	}
	if ( /(iPod|iPad|iPhone|Mac)/.test( userAgent ) ) {
		return 'Apple'
	}
	return 'unknown'
}

const getNetworkType = () => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const connection = ( navigator as any ).connection || {}
	return connection.effectiveType || 'unknown'
}

const getReferrer = () => {
	return document.referrer || null
}

const getLanguage = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().locale
	} catch ( e ) {
		// nothing
	}
	try {
		return navigator.language
	} catch ( e ) {
		// nothing
	}

	return 'N/A'
}

export const getDeviceData = ( opts: Partial<DeviceData> = {} ): DeviceData => {
	const osType = getOS()
	const osVersion = getOSVersion()
	const browser = getBrowser()
	const browserVersion = getBrowserVersion()
	const utm = collectUTM()
	const referrer = getReferrer()
	return {
		osType,
		osVersion,
		deviceType: getDeviceType(),
		deviceManufacturer: deviceManufacturer(),
		deviceClass: 'unknown',
		deviceModel: 'unknown',
		deviceLanguage: getLanguage(),
		browser,
		browserVersion,
		displayWidth: window.screen.availWidth,
		displayHeight: window.screen.availHeight,
		displayDpi: window.devicePixelRatio,
		networkType: getNetworkType(),
		sdkVersion: SDKVersion,
		sdkVersionNumber: SDKNumVersion,
		appVersion: 'unknown',
		utm: utm,
		referer: referrer,
		userAgent: navigator.userAgent,
		...opts,
	}
}

export const getDeviceDataMin = (): DeviceDataMin => {
	const osType = getOS()
	const osVersion = getOSVersion()
	const browser = getBrowser()
	const browserVersion = getBrowserVersion()
	const utm = collectUTM()
	const referrer = getReferrer()
	return {
		ot: osType,
		ov: osVersion,
		dt: getDeviceType(),
		dm: deviceManufacturer(),
		dw: window.screen.availWidth,
		dh: window.screen.availHeight,
		dd: window.devicePixelRatio,
		dc: 'unknown',
		dmo: 'unknown',
		br: browser,
		dl: navigator.language,
		bv: browserVersion,
		nt: getNetworkType(),
		sv: SDKVersion,
		svn: SDKNumVersion,
		av: 'unknown',
		utm,
		ref: referrer,
		ua: navigator.userAgent,
	}
}

export default class DomCollector {


	sendLog: ( log: string ) => void

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private debug = ( ..._args: unknown[] ) => {
		// console.debug( ...[ 'UXCamWeb:' ].concat( args as string[] ) )
	}
	// private error = ( ...args: unknown[] ) => {
	// 	console.error( ...[ 'UXCamWeb:' ].concat( args as string[] ) )
	// }
	// private warn = ( ...args: unknown[] ) => {
	// 	console.warn( ...[ 'UXCamWeb:' ].concat( args as string[] ) )
	// }
	// private info = ( ...args: unknown[] ) => {
	// 	console.info( ...[ 'UXCamWeb:' ].concat( args as string[] ) )
	// }
	// private log = ( ...args: unknown[] ) => {
	// 	console.log( ...[ 'UXCamWeb:' ].concat( args as string[] ) )
	// }

	private getSeq = ( reserve = 1 ) => {
		const currentSeq = this.seq
		const nextSeq = currentSeq + reserve
		this.seq = nextSeq
		this.onSeqUpdate( currentSeq )
		return currentSeq
	}

	private min: boolean

	private getTime = () => {
		if ( this.startTime === null ) {
			this.startTime = new Date().getTime()
			this.onNewStartTime( this.startTime )
			return 0
		}
		return new Date().getTime() - this.startTime
	}

	private onNewStartTime: ( startTime: number ) => void

	createID = () => {
		return `${ this.sessionCounter }_${ crypto.randomUUID() }`
	}

	__domMap: Map<string, HTMLElement> = ( () => {
		const map = new Map()
		map.set( '#document', document )
		map.set( '#doctype', document.doctype )
		map.set( '#html', document.documentElement )
		map.set( '#body', document.body )
		map.set( '#head', document.head )
		return map
	} )()
	__reverseDomMap: Map<HTMLElement, string> = ( () => {
		const map = new Map()
		map.set( document, '#document' )
		map.set( document.doctype, '#doctype' )
		map.set( document.documentElement, '#html' )
		map.set( document.body, '#body' )
		map.set( document.head, '#head' )
		return map
	} )()
	__styleSheetMap: Map<CSSStyleSheet, true> = new Map()

	registerElement = ( element: HTMLElement ) => {
		if ( this.__reverseDomMap.has( element ) ) {
			return
		}
		const uuid = this.createID().toString()
		this.__domMap.set( uuid, element )
		this.__reverseDomMap.set( element, uuid )
	}

	unregisterElement = ( element: HTMLElement ) => {
		const uuid = this.__reverseDomMap.get( element )
		if ( uuid === undefined ) {
			return
		}
		this.__reverseDomMap.delete( element )
		this.__domMap.delete( uuid )
	}

	scheduleUnregisterElement = ( element: HTMLElement | HTMLElement[] ) => {
		setTimeout( () => {
			if ( Array.isArray( element ) ) {
				for ( const el of element ) {
					this.unregisterElement( el )
				}
			} else {
				if ( !document.documentElement.contains( element ) ) {
					this.unregisterElement( element )
				}
			}
		} )
	}
		

	getElementByUUID = ( uuid: string, debug?: unknown ) => {
		const result = this.__domMap.get( uuid )
		if ( result === undefined ) {
			if ( debug ) {
				this.debug( 'Debug Error below', debug )
			}
			throw new Error( 'Could not find element in map, 2' )
		}
		return result
	}
	getUUIDByElement = ( element: HTMLElement, debug?: unknown ) => {
		const result = this.__reverseDomMap.get( element )
		if ( result === undefined ) {
			if ( debug ) {
				this.debug( 'Debug Error below', debug )
			}
			this.debug( 'Could not find element in reverse map', element )
			throw new Error( `Could not find element in reverse map, 2 ${ debug || '' }` )
		}
		return result
	}

	/**
	 * `onChange` is a function that will be called with the changes
	 */
	__onChange: ( onChange: DOMChanges | DOMChangesMin ) => void
	/**
	 * `onSeqUpadte` is a function that will be called with the new seq
	 */
	onSeqUpdate: ( seq: number ) => void
	/**
	 * `seq` is a number that will be incremented for each change
	 */
	seq: number = 0
	/**
	 * `startTime` is the time when the collection started
	 */
	startTime: number | null = 0

	/**
	 * `sessionCounter` is used for debugging
	 */
	sessionCounter: number

	occlusionOptions: OcclusionOptions

	appKey: string

	public destroy: () => void
	public abort: () => void

	constructor( { onChange, startTime, onSeqUpdate, onNewStartTime, seq, min, sendLog, sessionCounter, abort, appKey }: ICollect, options: OcclusionOptions ) {
		this.__onChange = onChange
		this.onSeqUpdate = onSeqUpdate
		this.onNewStartTime = onNewStartTime
		this.startTime = startTime
		this.seq = seq
		this.min = min
		this.occlusionOptions = options
		this.sendLog = sendLog
		this.sessionCounter = sessionCounter
		this.debug( 'DomCollector opts', options )
		this.destroy = this.init()
		this.abort = abort
		this.appKey = appKey
	}

	init = () => {
		const clearMutObserver = this.startMutationObserver()
		const clearInputObserver = this.startInputObserver()
		const clearUrlObserver = this.startUrlObserver()
		const clearInjectCSSObserver = this.injectCSSObserver()

		// clear mut work
		if ( this.idleMutWork ) {
			this.clearIdleCallback( this.idleMutWork )
		}

		return () => {
			clearMutObserver()
			clearInputObserver()
			clearUrlObserver()
			clearInjectCSSObserver()
		}
	}

	scheduleOnChangeEvent = ( onChange: DOMChanges | DOMChangesMin ) => {
		setTimeout( () => {
			this.__onChange( onChange )
		} )
	}

	private injectCSSObserver() {

		const originalInsertRule = CSSStyleSheet.prototype.insertRule
		const originalDeleteRule = CSSStyleSheet.prototype.deleteRule

		/**
		 * CSS rules that are dynamically added or removed are not captured by the MutationObserver
		 * We need to use the CSSStyleSheet.prototype.insertRule and CSSStyleSheet.prototype.deleteRule
		 * to capture these changes
		 */
		CSSStyleSheet.prototype.insertRule = new Proxy( CSSStyleSheet.prototype.insertRule, {
			apply: ( target, thisArg, args ) => {

				const [rule, index] = args

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const result = target.apply( thisArg, args as any )

				if ( this.__styleSheetMap.has( thisArg ) ) {
					const change: CSSInsertRule | CSSInsertRuleMin = this.min ? {
						s: this.getSeq(),
						t: this.getTime(),
						ct: ChangeType.CSS_INSERT_RULE,
						r: processCSSRule( rule ),
						i: index,
						tg: this.getUUIDByElement( thisArg.ownerNode as HTMLElement ),
					} : {
						seq: this.getSeq(),
						time: this.getTime(),
						type: 'CSS_INSERT_RULE',
						rule: processCSSRule( rule ),
						index,
						targetStyle: this.getUUIDByElement( thisArg.ownerNode as HTMLElement ),
					}

					this.__onChange( change )
				}

				return result
			},
		} )
		CSSStyleSheet.prototype.deleteRule = new Proxy( CSSStyleSheet.prototype.deleteRule, {
			apply: ( target, thisArg, args ) => {
				const [index] = args

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const result = target.apply( thisArg, args as any )

				if ( this.__styleSheetMap.has( thisArg ) ) {
					const change: CSSDeleteRule | CSSDeleteRuleMin = this.min ? {
						s: this.getSeq(),
						t: this.getTime(),
						ct: ChangeType.CSS_DELETE_RULE,
						i: index,
						tg: this.getUUIDByElement( thisArg.ownerNode as HTMLElement ),
					} : {
						seq: this.getSeq(),
						time: this.getTime(),
						type: 'CSS_DELETE_RULE',
						index,
						targetStyle: this.getUUIDByElement( thisArg.ownerNode as HTMLElement ),
					}
					this.__onChange( change )
				}

				return result
			},
		} )

		return () => {
			CSSStyleSheet.prototype.insertRule = originalInsertRule
			CSSStyleSheet.prototype.deleteRule = originalDeleteRule
		}
	}

	collectDocument = ( document: Document ): INode | INodeMin => {

		const childNodes: ( INode | INodeMin )[] = []
		for ( const child of document.childNodes ) {
			const node = this.collectElement( child as HTMLElement )
			childNodes.push( node as INode | INodeMin )
		}

		const node: INode | INodeMin = this.min ? {
			nn: '#document',
			nt: Node.DOCUMENT_NODE,
			t: '#document',
			a: {},
			u: '#document',
			c: childNodes as INodeMin[],
			r: [],
			s: false,
		} : {
			nodeName: '#document',
			tagName: '#document',
			attributes: {},
			UUID: '#document',
			nodeType: Node.DOCUMENT_NODE,
			cssRules: [],
			childNodes: childNodes as INode[],
			isSVG: false,
		}

		return node
	}

	collectTextNode = ( text: Text ) => {

		const UUID = this.getUUIDByElement( text as unknown as HTMLElement )

		let useTextContent = text.textContent || ''
		if ( text.parentNode && text.parentNode.nodeName === 'STYLE' ) {
			// if it's a style tag, we need to process the text content
			useTextContent = processCSSRule( text.textContent || '' )
		} else if ( text.parentNode && text.parentNode.nodeName === 'SCRIPT' ) {
			// if it's a script tag, we need to hide the text
			useTextContent = '/* script content */'
		} else if ( text.parentNode && this.isTextSensitive( text.parentNode as HTMLElement ) ) {
			// if it's a child of a sensitive element, we need to hide the text
			useTextContent = useTextContent.replace( /\w|\d/g, '*' )
		} else if ( text.parentNode && text.parentNode.nodeName != 'SCRIPT' && text.parentNode.nodeName != 'STYLE' ) {
			// if it's not a script tag or a style tag, we need to hide the numbers
			useTextContent = useTextContent.replace( /\d/g, '*' )
		}

		const node: INode | INodeMin = this.min ? {
			nn: '#text',
			nt: Node.TEXT_NODE,
			t: '#text',
			a: {
				textContent: useTextContent,
			},
			u: UUID,
			c: [],
			r: [],
			s: false,
		} : {
			nodeName: '#text',
			tagName: '#text',
			nodeType: Node.TEXT_NODE,
			cssRules: [],
			attributes: {
				textContent: useTextContent,
			},
			UUID,
			childNodes: [],
			isSVG: false,
		}

		return node
	}

	collectCommentNode = ( comment: Comment ) => {
		const node: INode | INodeMin = this.min ? {
			nn: '#comment',
			nt: Node.COMMENT_NODE,
			t: '#comment',
			a: {
				textContent: comment.textContent || '',
			},
			u: this.getUUIDByElement( comment as unknown as HTMLElement ),
			c: [],
			r: [],
			s: false,
		} : {
			nodeName: '#comment',
			tagName: '#comment',
			nodeType: Node.COMMENT_NODE,
			cssRules: [],
			attributes: {
				textContent: comment.textContent || '',
			},
			UUID: this.getUUIDByElement( comment as unknown as HTMLElement ),
			childNodes: [],
			isSVG: false,
		}

		return node
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	collectDocumentTypeNode = ( _documentType: DocumentType ) => {
		const node: INode | INodeMin = this.min ? {
			nn: '#doctype',
			nt: Node.DOCUMENT_TYPE_NODE,
			t: '#doctype',
			a: {},
			u: '#doctype',
			c: [],
			r: [],
			s: false,
		} : {
			nodeName: '#doctype',
			tagName: '#doctype',
			nodeType: Node.DOCUMENT_TYPE_NODE,
			cssRules: [],
			attributes: {
			},
			UUID: '#doctype',
			childNodes: [],
			isSVG: false,
		}

		return node
	}

	private getSourceURL = ( path: string ) => {
		// check if it's a relative path, e.g: style.css
		// if so, we need to add the window.location.origin
		// e.g: style.css -> http://localhost:3000/style.css
		// e.g: /style.css -> http://localhost:3000/style.css
		// e.g: css/style.css -> http://localhost:3000/css/style.css
		// @note: also check for data:image
		if ( !path.match( /^http/ ) && !path.match( /^data:/ ) ) {
			// check for pure relative path
			if ( !path.startsWith( '/' ) ) {
				// handle relative path
				// e.g: style.css, path: /index.html -> http://localhost:3000/style.css
				// e.g: style.css, path: /folder/index.html -> http://localhost:3000/style.css
				// e.g: css/style.css, path: /folder/index.html -> http://localhost:3000/css/style.css
				path = `${window.location.origin}/${ path }`
			} else if ( path.startsWith( '//' ) ){
				// check for path starting with //
				path = `${window.location.protocol}${ path }`
			} else {
				// check for path starting with /
				path = `${window.location.origin}${ path }`
			}
		}

		return path
	}

	collectElementNode = ( element: HTMLElement, opts?: {
		backTrackedNodes: Map<Node, IRemoveNodeMutation[]>,
		directlyAddedNodesMap: Map<Node, true>,
	} ) => {
		const UUID = this.getUUIDByElement( element )
		const min = this.min

		const node: INode | INodeMin = min ? {
			nn: element.nodeName,
			nt: Node.ELEMENT_NODE,
			t: element.tagName,
			a: {},
			u: UUID,
			c: [],
			r: [],
			s: element instanceof SVGElement,
		} : {
			nodeName: element.nodeName,
			tagName: element.tagName,
			nodeType: Node.ELEMENT_NODE,
			attributes: {},
			cssRules: [],
			UUID,
			childNodes: [],
			isSVG: element instanceof SVGElement,
		}

		const isOccluded = !!element.closest( '[data-uxc="obfuscated"]')

		// check if it's an image or video
		if ( element.nodeName === 'IMG' || element.nodeName === 'VIDEO' ) {
			// check if there's a natural width and height
			if ( ( element as HTMLImageElement ).naturalWidth || ( element as HTMLVideoElement ).videoWidth ) {
				const width = element.nodeName === 'IMG' ? ( element as HTMLImageElement ).naturalWidth : ( element as HTMLVideoElement ).videoWidth
				const height = element.nodeName === 'IMG' ? ( element as HTMLImageElement ).naturalHeight : ( element as HTMLVideoElement ).videoHeight
				const type = element.nodeName === 'IMG' ? INodeMeta.IMG : INodeMeta.VIDEO

				const meta: IMGMeta | VideoMeta | IMGMetaMin | VideoMetaMin = min ? {
					type,
					w: width,
					h: height,
				} as IMGMetaMin | VideoMetaMin : {
					type,
					width,
					height,
				} as IMGMeta | VideoMeta

				if ( min ) {
					( node as INodeMin ).m = meta as IMGMetaMin | VideoMetaMin
				} else {
					( node as INode ).meta = meta as IMGMeta | VideoMeta
				}

			} else {
				// if it's an image or video but the natural width and height are not available
				// we need to wait for the load event
				if ( element.nodeName === 'IMG' ) {
					const handleLoad = () => {
						this.handleMetaLoad( element as HTMLImageElement | HTMLVideoElement )
						element.removeEventListener( 'load', handleLoad )
					}
					element.addEventListener( 'load', handleLoad )
				} else if ( element.nodeName === 'VIDEO' ) {
					const handleLoadedData = () => {
						this.handleMetaLoad( element as HTMLImageElement | HTMLVideoElement )
						element.removeEventListener( 'loadedmetadata', handleLoadedData )
					}
					element.addEventListener( 'loadedmetadata', handleLoadedData )
				}
			}
		}

		// check for scroll position
		if ( element.scrollTop || element.scrollLeft ) {

			const scrollChange: ScrollChange | ScrollChangeMin = min ? {
				s: this.getSeq(),
				t: this.getTime(),
				ct: ChangeType.SCROLL,
				tg: UUID,
				st: element.scrollTop,
				sl: element.scrollLeft,
			} : {
				seq: this.getSeq(),
				time: this.getTime(),
				type: 'SCROLL',
				target: UUID,
				scrollTop: element.scrollTop,
				scrollLeft: element.scrollLeft,
			}

			this.__onChange( scrollChange )
		}

		/**
		 * check if it's a svg element
		 * if so, we need to add the namespace
		 * https://stackoverflow.com/questions/19610089/why-does-svg-need-a-namespace-attribute
		 * https://stackoverflow.com/questions/3642035/jquerys-append-not-working-with-svg-element
		 * 
		 */
		if ( element instanceof SVGElement ) {
			if ( min ) {
				( node as INodeMin ).a.xmlns = 'http://www.w3.org/2000/svg'
			} else {
				( node as INode ).attributes.xmlns = 'http://www.w3.org/2000/svg'
			}
		}

		/**
		 * if it's a script we just want to ignore the attributes
		 */
		if ( element.nodeName === 'SCRIPT' ) {
			// do nothing
		} else {
			const occludeMediaSource = isOccluded && ( element.nodeName === 'IMG' || element.nodeName === 'VIDEO' )
			for ( let i = 0; i < element.attributes.length; i++ ) {

				// if element is an occluded image or video, we need to obfuscate the src
				const attr = element.attributes[i]

				let useValue = attr.value
				if ( occludeMediaSource ) {
					continue
				}

				// obfuscate input values for sensitive fields
				if ( element.nodeName === 'INPUT' && attr.name === 'value' && ( this.isSensitiveInput( element ) || this.isElementUXCamObfuscated( element ) ) ) {
					if ( ( element as HTMLInputElement ).type === 'number' ) {
						useValue = '000000'
					} else {
						useValue = '******'
					}
				}

				/**
				 * adjust path to images and other assets
				 * replace /path with ${window.location.origin}/path
				 */

				if ( attr.name === 'src' || attr.name === 'href' ) {
					// check if it's a base64 image and larger than 1000 characters
					// if so, we will not collect it
					if ( useValue.startsWith( 'data:image' ) && useValue.length > 1000 ) {
						continue
					}

					// check if it's a relative path, e.g: style.css
					// if so, we need to add the window.location.origin
					// e.g: style.css -> http://localhost:3000/style.css
					// e.g: /style.css -> http://localhost:3000/style.css
					// @note: also check for data:image 
					if ( attr.name === 'src' ) {
						useValue = ( element as HTMLImageElement ).src
					} else if ( attr.name === 'href' ) {
						useValue = ( element as HTMLAnchorElement ).href
					} else {
						useValue = this.getSourceURL( useValue )
					}
				}
				if ( attr.name === "srcset" && useValue ) {
					// @note: all this split might fail, in case the src set is not set properly
					// so just to not break things even more, will wrap it in a try catch
					try {
						const srcset = useValue.split( ',' ).map( ( src ) => {
							const [url, descriptor] = src.trim().split( ' ' )
							return `${this.getSourceURL( url )} ${descriptor}`
						} )
						useValue = srcset.join( ', ' )
					} catch ( e ) {
						// nothing
					}
				}

				if ( min ) {
					( node as INodeMin ).a[attr.name] = useValue
				} else {
					( node as INode ).attributes[attr.name] = useValue
				}
			}
		}

		const childNodes: ChildNode[] = Array.from( element.childNodes )
		// for ( const child of element.childNodes ) {
		// 	if ( opts?.directlyAddedNodesMap.has( child ) ) {
		// 		continue
		// 	}
		// 	childNodes.push( child as ChildNode )
		// }

		const backtrackedChildNodes = opts?.backTrackedNodes && opts.backTrackedNodes.get( element )

		if ( backtrackedChildNodes ) {
			for ( const nodeMut of backtrackedChildNodes ) {
				const {
					prevSibling,
					node,
				} = nodeMut

				const insertIndex = prevSibling ? childNodes.indexOf( prevSibling as ChildNode ) + 1 : childNodes.length
				childNodes.splice( insertIndex, 0, node as ChildNode )
			}
		}

		for ( const child of childNodes ) {
			const childNode = this.collectElement( child as HTMLElement, opts )
			if ( min ) {
				( node as INodeMin ).c.push( childNode as INodeMin )
			} else {
				( node as INode ).childNodes.push( childNode as INode )
			}
		}

		if ( element.nodeName === 'STYLE' ) {
			/**
			 * Check for rules, some of the rules might be dynamically added
			 * and not captured by the MutationObserver
			 */

			const sheet = ( element as HTMLStyleElement ).sheet
			if ( sheet && !element.innerText.trim() && sheet.cssRules.length ){

				const length = sheet.cssRules.length
				for ( let index = 0; index < length; index++ ) {
					const rule = sheet.cssRules[index]
					if ( min ) {
						( node as INodeMin ).r.push( {
							r: processCSSRule( rule.cssText || '' ),
							i: index,
							t: UUID,
						} )
					} else {
						( node as INode ).cssRules.push( {
							rule: processCSSRule( rule.cssText || '' ),
							index,
							targetStyle: UUID,
						} )
					}
				}
				this.__styleSheetMap.set( sheet, true )
			}

		}

		return node
	}

	collectElement = ( element: HTMLElement, opts?: {
		backTrackedNodes: Map<Node, IRemoveNodeMutation[]>,
		directlyAddedNodesMap: Map<Node, true>,
	} ) => {

		const { nodeType } = element

		this.registerElement( element )
		// eslint-disable-next-line @typescript-eslint/no-explicit-any

		switch( nodeType ) {
			case Node.DOCUMENT_NODE: {
				return this.collectDocument( element as unknown as Document )
			}
			case Node.ELEMENT_NODE: {
				return this.collectElementNode( element as HTMLElement, opts )
			}
			case Node.TEXT_NODE: {
				return this.collectTextNode( element as unknown as Text )
			}
			case Node.COMMENT_NODE: {
				return this.collectCommentNode( element as unknown as Comment )
			}
			case Node.DOCUMENT_TYPE_NODE: {
				return this.collectDocumentTypeNode( element as unknown as DocumentType )
			}
			default: {
				throw new Error( 'Unknown node type' )
			}
		}

	}


	backtrackMutations = ( mutations: MutationRecord[] ): {
		directlyAddedNodesMap: Map<Node, true>,
		removedNodesMap: Map<Node, IRemoveNodeMutation[]>,
	} => {
		const removedNodesMap: Map<Node, IRemoveNodeMutation[]> = new Map()
		const directlyAddedNodesMap: Map<Node, true> = new Map()

		for( const mutation of mutations ) {
			if ( mutation.type !== 'childList' ) { continue }
			for ( const addedNode of mutation.addedNodes ) {
				directlyAddedNodesMap.set( addedNode, true )
			}
			for( const removedNode of mutation.removedNodes ) {
				const list = removedNodesMap.get( mutation.target ) || []
				list.push( {
					parent: mutation.target,
					node: removedNode,
					prevSibling: mutation.previousSibling,	
					nextSibling: mutation.nextSibling,
				} )
				removedNodesMap.set( mutation.target, list )
			}
		}

		return {
			removedNodesMap,
			directlyAddedNodesMap,
		}

	}

	isElementUXCamObfuscated = ( element: HTMLElement ) => {
		const uxc = element.getAttribute( 'data-uxc' )
		return uxc === 'obfuscated'
	}

	isAttributeChangeSensitive = ( mutation: MutationRecord ) => {
		if ( mutation.type !== 'attributes' ) {
			return false
		}
		const { attributeName, target } = mutation
		if ( attributeName !== 'value' ) {
			return false
		}
		if ( !( target instanceof HTMLInputElement ) ) {
			return false
		}

		if ( ( target as HTMLElement ).matches( this.sensitiveTypeSelector ) ) {
			return true
		}

		if ( ( target as HTMLElement ).matches( this.sensitiveNameSelector ) ) {
			return true
		}

		if ( ( target as HTMLElement ).matches( this.sensitiveAutoCompleteSelector ) ) {
			return true
		}

		return false
	}

	private sensitiveTypeSelector = [
		'password',
		'email',
		'tel',
		'hidden',
		'number',
		'hidden',
	].map( ( type ) => `[type="${type}"]` ).join( ', ' )

	private sensitiveNameSelector = [
		'password',
		'cc-',
		'email',
		'phone',
	].map( ( name ) => `[name*="${name}"]` ).join( ', ' )

	private sensitiveAutoCompleteSelector = [
		'cc-',
		'address',
		'phone',
		'email',
		'password',
	].map( ( autocomplete ) => `[autocomplete*="${autocomplete}"]` ).join( ', ' )

	isSensitiveInput = ( element: HTMLElement ) => {
		const isSensitiveType = element.matches( this.sensitiveTypeSelector ) || element.matches( '[data-uxc="obfuscated"]' )
		if ( isSensitiveType ) {
			return true
		}

		if ( element.matches( this.sensitiveNameSelector ) ) {
			return true
		}

		if ( element.matches( this.sensitiveAutoCompleteSelector ) ) {
			return true
		}

		return false
	}

	isTextSensitive = ( node: Node ) => {

		// if parent is a style tag or a script tag it's not sensitive
		if ( node.parentNode && ( node.parentNode?.nodeName === 'STYLE' || node.parentNode?.nodeName === 'SCRIPT' ) ) {
			return false
		}

		// check if there's a parent with a data-uxc attribute
		const sensitiveParent = ( node as HTMLElement ).closest( '[data-uxc="obfuscated"]')
		if ( sensitiveParent ) {
			return true
		}

		return false
	}

	private mutCollection: SequencedMutationRecord[] = []

	private MUT_MAX_WAIT_TIME = 3_000
	private scheduledTimestamp: number | null = null
	private static saveIdleCallback = ( cb: () => void, timeout = 500 ) => {
		return typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback( cb, {
			timeout,
		} ) : setTimeout( cb, timeout )
	}

	private clearIdleCallback = ( idle: number | Timer ) => {
		if ( typeof window.cancelIdleCallback === 'function' ) {
			window.cancelIdleCallback( idle as number )
		} else {
			clearTimeout( idle as Timer )
		}
	}

	private idleMutWork: number | Timer | null = null
	private scheduleMutationWork = () => {
		if ( !this.scheduledTimestamp ) {
			this.scheduledTimestamp = new Date().getTime()
		}

		if ( this.idleMutWork ) {
			this.clearIdleCallback( this.idleMutWork )
			this.idleMutWork = null
		}

		const now = new Date().getTime()
		const diff = now - this.scheduledTimestamp

		const handleMutWork = () => {
			this.mutationWork()
			this.scheduledTimestamp = null
		}

		if ( diff > this.MUT_MAX_WAIT_TIME ) {
			handleMutWork()
			return
		}

		this.idleMutWork = DomCollector.saveIdleCallback( handleMutWork )
	}

	// private __internalFlagged = false

	private backFillElement = ( element: HTMLElement, seq: number, time: number ) => {
		// check if element is present in the dom
		if ( !document.documentElement.contains( element ) || this.__reverseDomMap.has( element ) ) {
			return
		}

		// if ( !this.__internalFlagged ) {
		// 	this.handleEvent( 'uxcam_internal_alpha' )
		// 	this.__internalFlagged = true
		// }

		const parentNode = element.parentNode
		const prevSiblingNode = element.previousSibling

		// backfilling this would require recursively backfilling the parent nodes, might be complicated
		if ( !this.__reverseDomMap.has( parentNode as HTMLElement ) ) {
			// find last parent node that's not in the dom yet
			let parent = parentNode
			while ( parent && !this.__reverseDomMap.has( parent as HTMLElement ) ) {
				parent = parent.parentNode
			}
			if ( parent ) {
				const parentNode = this.collectElement( parent as HTMLElement )
				const parentUUID = this.getUUIDByElement( ( parent as HTMLElement ).parentNode! as HTMLElement, 'backfill parent recv' )
				const parentPrevSibling = parent.previousSibling

				if ( parentPrevSibling && !this.__reverseDomMap.has( parentPrevSibling as HTMLElement ) ) {
					const addSiblings = []
					let sibling: ChildNode | null = parentPrevSibling
					while ( sibling && !this.__reverseDomMap.has( sibling as HTMLElement ) ) {
						addSiblings.push( sibling as HTMLElement )
						sibling = sibling.previousSibling
					}

					for ( const sib of addSiblings.reverse() ) {
						const node = this.collectElement( sib as HTMLElement )
						const sibPrevSibNode = sib.previousSibling
						const prevSiblingUUID = sibPrevSibNode ? this.getUUIDByElement( sibPrevSibNode as HTMLElement, 'backfill parent sibl recv' ) : null

						const change: AddNodes | AddNodesMin = this.min ? {
							s: seq,
							t: time,
							ct: ChangeType.ADD_NODES,
							p: parentUUID,
							c: [
								node as INodeMin,
							],
							pu: prevSiblingUUID,
							nu: null,
						} : {
							seq,
							time,
							type: 'ADD_NODES',
							parentUUID,
							children: [
								node as INode,
							],
							prevUUID: prevSiblingUUID,
							nextUUID: null,
						}

						this.__onChange( change )
					}
				}

				const parentPrevSiblingUUID = parentPrevSibling ? this.getUUIDByElement( parentPrevSibling as HTMLElement, 'backfill parent prev sibl' ) : null

				const change: AddNodes | AddNodesMin = this.min ? {
					s: seq,
					t: time,
					ct: ChangeType.ADD_NODES,
					p: parentUUID,
					c: [
						parentNode as INodeMin,
					],
					pu: parentPrevSiblingUUID,
					nu: null,
				} : {
					seq,
					time,
					type: 'ADD_NODES',
					parentUUID,
					children: [
						parentNode as INode,
					],
					prevUUID: parentPrevSiblingUUID,
					nextUUID: null,
				}

				this.__onChange( change )

			}
		}

		if ( !this.__reverseDomMap.has( parentNode as HTMLElement ) ) {
			this.sendLog( 'Backfilling nodes with missing parent' )
		}

		const parentUUID = this.getUUIDByElement( parentNode as HTMLElement, 'backfill parent' )
		
		// same as above, but for siblings
		if ( prevSiblingNode && !this.__reverseDomMap.has( prevSiblingNode as HTMLElement ) ) {
			let sibling = prevSiblingNode
			const addSiblings: HTMLElement[] = []
			while ( sibling && !this.__reverseDomMap.has( sibling as HTMLElement ) ) {
				addSiblings.push( sibling as HTMLElement )
				sibling = sibling.previousSibling as HTMLElement
			}
			for ( const sib of addSiblings.reverse() ) {
				const node = this.collectElement( sib as HTMLElement ) as INode | INodeMin
				const sibPrevSibNode = sib.previousSibling
				const prevSiblingUUID = sibPrevSibNode ? this.getUUIDByElement( sibPrevSibNode as HTMLElement, 'backfill sibl recv' ) : null

				const change: AddNodes | AddNodesMin = this.min ? {
					s: seq,
					t: time,
					ct: ChangeType.ADD_NODES,
					p: parentUUID,
					c: [
						node as INodeMin,
					],
					pu: prevSiblingUUID,
					nu: null,
				} : {
					seq,
					time,
					type: 'ADD_NODES',
					parentUUID,
					children: [
						node as INode,
					],
					prevUUID: prevSiblingUUID,
					nextUUID: null,
				}

				this.__onChange( change )
			}
		}

		if (
			( prevSiblingNode && !this.__reverseDomMap.has( prevSiblingNode as HTMLElement ) )
			// next sibling not required?
			// || ( nextSiblingNode && !this.__reverseDomMap.has( nextSiblingNode as HTMLElement ) )
		) {
			this.sendLog( 'Backfilling nodes with missing siblings' )
		}

		const node = this.collectElement( element as HTMLElement ) as INode | INodeMin

		const prevSiblingUUID = prevSiblingNode ? this.getUUIDByElement( prevSiblingNode as HTMLElement, 'backfill sibl' ) : null
		// const nextSiblingUUID = nextSiblingNode ? this.getUUIDByElement( nextSiblingNode as HTMLElement ) : null

		const change: AddNodes | AddNodesMin = this.min ? {
			s: seq,
			t: time,
			ct: ChangeType.ADD_NODES,
			p: parentUUID,
			c: [
				node as INodeMin,
			],
			pu: prevSiblingUUID,
			nu: null,
		} : {
			seq,
			time,
			type: 'ADD_NODES',
			parentUUID,
			children: [
				node as INode,
			],
			prevUUID: prevSiblingUUID,
			nextUUID: null,
		}

		this.__onChange( change )
	}

	private mutationWork = () => {

		try {
			/**
			 * We will get notified about mutations in the target element
			 * But not about mutations sub in the children
			 * Important!: If a sub child node was removed, we won't find it
			 * in the sub child childNodes, to handle that we would need to
			 * check the removedNodes and adjust the addedNodes accordingly
			 */

			const mutCollection = Array.from( this.mutCollection )
			// reset the collection
			this.mutCollection = []

			const {
				directlyAddedNodesMap,
				removedNodesMap: backTrackedNodes,
			} = this.backtrackMutations( mutCollection )

			for( const mutation of mutCollection ) {
				const { type, target, seq, time } = mutation

				switch( type ) {
					case 'attributes': {
						if ( !this.__reverseDomMap.has( target as HTMLElement ) ) {
							if ( !document.documentElement.contains( target ) ) {
								continue
							}
							// add the target element as Added nodes
							try {
								this.backFillElement( target as HTMLElement, seq, time )
							} catch( e: unknown ) {
								this.sendLog( `Backfill: ${ ( e as Error ).message || 'Backfilling failed' }` )
								this.abort()
								return
							}

							// nodes were added to an previously unknown element, this shouldn't happen
							if ( !this.__reverseDomMap.has( target as HTMLElement ) && !sessionStorage.getItem( 'uxc:debug:addedNodesToUnknownElement' ) ) {
								this.sendLog( 'Nodes were added to an previously unknown element' )
								sessionStorage.setItem( 'uxc:debug:addedNodesToUnknownElement', '1' )
							}
						}
						const { attributeName } = mutation as MutationRecord & { attributeName: string }
						const value = ( target as HTMLElement ).getAttribute( attributeName )
						let useValue = value

						// process urls
						if ( typeof value === 'string' && ( attributeName === 'src' || attributeName === 'href' ) ) {
							// check for base64 images
							if ( value.startsWith( 'data:image' ) && value.length > 1000 ) {
								continue
							}
							useValue = useValue!.replace( /^\//, `${window.location.origin}/` )
						}

						// skip transform animations
						if ( attributeName === 'transform' ) {
							continue
						}

						if ( this.isAttributeChangeSensitive( mutation ) ) {
							useValue = '******'
						}

						// check if it's a occluded video or image
						if ( this.isElementUXCamObfuscated( target as HTMLElement ) && attributeName === 'src' ) {
							// this.handleMetaLoad( target as HTMLImageElement | HTMLVideoElement )
							const handleLoad = () => {
								this.handleMetaLoad( target as HTMLImageElement | HTMLVideoElement )
								target.removeEventListener( 'load', handleLoad )
							}
							target.addEventListener( 'load', handleLoad )
							continue
						}

						// check if element was collected
						if ( !this.__reverseDomMap.has( target as HTMLElement ) ) {
							this.debug( 'element not collected', target, mutCollection )
							continue
						}
						// check if it's a script src
						if ( attributeName === 'src' && ( target as HTMLElement ).nodeName === 'SCRIPT' ) {
							continue
						}

						const change: AttrChange | AttrChangeMin = this.min ? {
							s: seq,
							t: time,
							ct: ChangeType.ATTR_CHANGE,
							tg: this.getUUIDByElement( target as HTMLElement, mutation ),
							a: attributeName,
							v: useValue,
						} : {
							seq,
							time,
							type: 'ATTR_CHANGE',
							target: this.getUUIDByElement( target as HTMLElement, mutation ),
							attr: attributeName,
							value: useValue,
						}

						this.__onChange( change )
						break
					}
					case 'childList': {
						const {
							addedNodes: rawAddedNodes,
							removedNodes: rawRemovedNodes,
						} = mutation

						const addedNodes = Array.from( rawAddedNodes )

						const removedNodes = Array.from( rawRemovedNodes )

						if ( addedNodes.length ) {
							if ( !this.__reverseDomMap.has( target as HTMLElement ) ) {
								if ( !document.documentElement.contains( target ) ) {
									continue
								}
								// add the target element as Added nodes
								try {
									this.backFillElement( target as HTMLElement, seq, time )
								} catch( e: unknown ) {
									// this.sendLog( ( e as Error ).message || 'Backfilling failed' )
									this.sendLog( `Backfill: ${ ( e as Error ).message || 'Backfilling failed' }` )
									this.abort()
									return
								}

								// nodes were added to an previously unknown element, this shouldn't happen
								if ( !this.__reverseDomMap.has( target as HTMLElement ) && !sessionStorage.getItem( 'uxc:debug:addedNodesToUnknownElement' ) ) {
									this.sendLog( 'Nodes were added to an previously unknown element' )
									sessionStorage.setItem( 'uxc:debug:addedNodesToUnknownElement', '1' )
								}
							}
							this.registerElement( target as HTMLElement )

							let previousSibling: Node | null = mutation.previousSibling
							let nextSibling: Node | null = mutation.nextSibling

							const parentUUID = this.getUUIDByElement( target as HTMLElement, mutation )
							const childNodes: ( INode | INodeMin )[] = []

							for ( const child of addedNodes ) {
								const node = this.collectElement( child as HTMLElement, {
									backTrackedNodes,
									directlyAddedNodesMap,
								} ) as INode | INodeMin
								childNodes.push( node )
							}


							while( previousSibling ) {
								if ( this.__reverseDomMap.has( previousSibling as HTMLElement ) ) {
									break
								}
								childNodes.unshift( this.collectElement( previousSibling as HTMLElement, {
									backTrackedNodes,
									directlyAddedNodesMap,
								} ) )
								previousSibling = previousSibling.previousSibling
							}

							while( nextSibling ) {
								if ( this.__reverseDomMap.has( nextSibling as HTMLElement ) ) {
									break
								}
								childNodes.push( this.collectElement( nextSibling as HTMLElement, {
									backTrackedNodes,
									directlyAddedNodesMap,
								} ) )
								nextSibling = nextSibling.nextSibling
							}

							const prevUUID = previousSibling ? this.getUUIDByElement( previousSibling as HTMLElement, mutation ) : null
							const nextUUID = nextSibling ? this.getUUIDByElement( nextSibling as HTMLElement, mutation ) : null

							const change: AddNodes | AddNodesMin = this.min ? {
								s: seq,
								t: time,
								ct: ChangeType.ADD_NODES,
								p: parentUUID,
								c: childNodes as INodeMin[],
								pu: prevUUID,
								nu: nextUUID,
							} : {
								seq,
								time,
								type: 'ADD_NODES',
								parentUUID,
								children: childNodes as INode[],
								prevUUID,
								nextUUID,
							}

							this.__onChange( change )

						}

						if ( removedNodes.length ) {

							const removeUUIDs: string[] = []
							for ( const node of removedNodes ) {
								try {
									const uuid = this.getUUIDByElement( node as HTMLElement, mutation )
									removeUUIDs.push( uuid )
								} catch ( e ) {
									this.debug( e, 'Node not found', node )
									// ignore
								}
							}

							const change: RemoveNodes | RemoveNodesMin = this.min ? {
								s: seq,
								t: time,
								ct: ChangeType.REMOVE_NODES,
								u: removeUUIDs,
							} : {
								seq,
								time,
								type: 'REMOVE_NODES',
								UUIDs: removeUUIDs,
							}

							// iterate over all removedNode
							const unregisterElements: HTMLElement[] = []
							for ( const removedNode of removedNodes ) {
								unregisterElements.push( removedNode as HTMLElement )
								// iterater over children
								const iterator = document.createNodeIterator( removedNode, NodeFilter.SHOW_ALL )
								let currentNode
								while( ( currentNode = iterator.nextNode() ) ) {
									unregisterElements.push( currentNode as HTMLElement )
								}
							}
							this.__onChange( change )
							this.scheduleUnregisterElement( unregisterElements )
						}
						break
					}
					case 'characterData': {
						if ( !document.documentElement.contains( target ) ) {
							continue
						}
						if ( !this.__reverseDomMap.has( target as HTMLElement ) ) {
							try {
								this.backFillElement( target as HTMLElement, seq, time )
							} catch ( e: unknown ) {
								this.sendLog( ( e as Error ).message || 'Backfilling failed' )
								this.abort()
								return
							}
						}
						const { textContent } = mutation.target as HTMLElement
						let useTextContent = textContent || ''

						if ( mutation.target.parentNode && mutation.target.parentNode.nodeName === 'STYLE' ) {
							useTextContent = processCSSRule( useTextContent )
						} else if ( this.isTextSensitive( mutation.target.parentNode as HTMLElement ) ) {
							useTextContent = useTextContent.replace( /\w|\d/g, '*' )
						} else {
							useTextContent = useTextContent.replace( /\d/g, '*' )
						}

						const change: CharacterDataChange | CharacterDataChangeMin = this.min ? {
							s: seq,
							t: time,
							ct: ChangeType.CHARACTER_DATA_CHANGE,
							tg: this.getUUIDByElement( target as HTMLElement, mutation ),
							tc: useTextContent,
						} : {
							seq,
							time,
							type: 'CHARACTER_DATA_CHANGE',
							target: this.getUUIDByElement( target as HTMLElement, mutation ),
							textContent: useTextContent,
						}

						this.__onChange( change )
						break
					}
				}

			}

		} catch ( e: unknown ) {
			// this.sendLog( ( e as Error ).message )
			console.error( e )
			throw e
		}

	}

	onMutation = ( mutations: MutationRecord[] ) => {
		if ( !this.pageCollected ) {
			this.debug( 'collecting page from mutation' )
			this.collectDOM()
		}

		for( const mutation of mutations ) {
			const useMut = mutation as SequencedMutationRecord
			useMut.seq = this.getSeq()
			useMut.time = this.getTime()
			this.mutCollection.push( useMut )
		}

		this.debug( 'mutation' )
		this.scheduleMutationWork()

	}

	handleScroll = ( event: Event ) => {

		if ( !this.__reverseDomMap.has( event.target as HTMLElement ) ) {
			return
		}

		let scrollTop, scrollLeft
		let target: string
		if ( event.target === document || event.target === document.documentElement ) {
			scrollTop = document.documentElement.scrollTop
			scrollLeft = document.documentElement.scrollLeft
			target = this.getUUIDByElement( document.documentElement )
		} else {
			scrollTop = ( event.target as HTMLElement ).scrollTop
			scrollLeft = ( event.target as HTMLElement ).scrollLeft
			target = this.getUUIDByElement( event.target as HTMLElement )
		}

		const change: ScrollChange | ScrollChangeMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.SCROLL,
			tg: target,
			st: scrollTop,
			sl: scrollLeft,
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'SCROLL',
			target: target,
			scrollTop,
			scrollLeft,
		}

		this.__onChange( change )
	}

	handleFocus = ( event: Event ) => {
		if ( !this.__reverseDomMap.has( event.target as HTMLElement ) ) {
			return
		}

		const change: FocusChange | FocusChangeMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.FOCUS,
			tg: this.getUUIDByElement( event.target as HTMLElement ),
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'FOCUS',
			target: this.getUUIDByElement( event.target as HTMLElement ),
		}

		this.__onChange( change )
	}

	handleMouseMove = ( event: MouseEvent ) => {
		if ( !this.__reverseDomMap.has( event.target as HTMLElement ) ) {
			return
		}
		const change: MouseMoveChange | MouseMoveChangeMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.MOUSE_MOVE,
			tg: this.getUUIDByElement( event.target as HTMLElement ),
			x: event.clientX,
			y: event.clientY,
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'MOUSE_MOVE',
			target: this.getUUIDByElement( event.target as HTMLElement ),
			x: event.clientX,
			y: event.clientY,
		}

		this.__onChange( change )
	}

	handleMetaLoad = ( element: HTMLVideoElement | HTMLImageElement ) => {
		const width = element.nodeName === 'IMG' ? ( element as HTMLImageElement ).naturalWidth : ( element as HTMLVideoElement ).videoWidth
		const height = element.nodeName === 'IMG' ? ( element as HTMLImageElement ).naturalHeight : ( element as HTMLVideoElement ).videoHeight
		const type = element.nodeName === 'IMG' ? INodeMeta.IMG : INodeMeta.VIDEO

		const meta: IMGMeta | VideoMeta | IMGMetaMin | VideoMetaMin = this.min ? {
			type,
			w: width,
			h: height,
		} as IMGMetaMin | VideoMetaMin : { 
			type,
			width,
			height,
		} as IMGMeta | VideoMeta

		try {
			const change: MediaInfo | MediaInfoMin = this.min ? {
				s: this.getSeq(),
				t: this.getTime(),
				ct: ChangeType.MEDIA_INFO,
				tg: this.getUUIDByElement( element ),
				m: meta as IMGMetaMin | VideoMetaMin,
			} : {
				seq: this.getSeq(),
				time: this.getTime(),
				type: 'MEDIA_INFO',
				target: this.getUUIDByElement( element ),
				meta: meta as IMGMeta | VideoMeta,
			}
	
			this.__onChange( change )
		} catch( e ) {
			return
		}

	}

	private getElementSelector = ( element: HTMLElement ) => {
		if (!(element instanceof Element)) {
			return 'N/A';
		}
		
		const parts: string[] = [];
		
		while (element) {
			let part = "";
			const tagName = element.tagName.toLowerCase();
		
			if (element.id) {
				part = `#${element.id}`;
				parts.unshift(part);
				break; // ID is unique, no need to go further
			} else if (element.classList.length > 0) {
				const classes = Array.from(element.classList).map(cls => `.${cls}`).join('');
				part = `${tagName}${classes}`;
		
				// Check for uniqueness at this level (important optimization)
				const siblings = Array.from(element.parentNode!.children).filter(el => el.tagName.toLowerCase() === tagName && el.classList.toString() === element.classList.toString());
		
				if (siblings.length > 1) {
					const index = Array.from(element.parentNode!.children).indexOf(element) + 1;
					part += `:nth-child(${index})`;
				}
		
				parts.unshift(part);
		
			} else {
				part = tagName;
				const siblings = Array.from(element.parentNode!.children).filter(el => el.tagName.toLowerCase() === tagName);
				if (siblings.length > 1) {
					const index = Array.from(element.parentNode!.children).indexOf(element) + 1;
					part += `:nth-child(${index})`;
				}
				parts.unshift(part);
		
			}
		
			element = element.parentNode as HTMLElement;
			if (!element || element.tagName === 'HTML') break; // Stop at the HTML element
		}
		
		return parts.join(" > "); // Or just parts.join('') for a more concise selector
	}

	handleEvent = ( name: string, payload = {} ) => {
		const change: SessionEvent | SessionEventMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.EVENT,
			n: name,
			p: payload,
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'EVENT',
			name,
			properties: payload,
		}

		this.__onChange( change )
	
	}

	clickQueue: {
		x: number,
		y: number,
		pgX: number,
		pgY: number,
		target: string,
		selector: string,
		s: number,
		t: number,
		activity: string,
		isResponsive: boolean,
		isRage: boolean,
		type: ChangeType.MOUSE_CLICK | ChangeType.MOUSE_DOUBLE_CLICK,
		ignore?: boolean, // will be used to count double clicks as separate clicks
	}[] = []

	scheduledClickWork: Timer | null = null

	scheduleClickWork = () => {
		if ( this.scheduledClickWork ) {
			clearTimeout( this.scheduledClickWork )
			this.scheduledClickWork = null
		}

		this.scheduledClickWork = setTimeout( this.clickWork, 500 )
	}

	// check if elements are focusable
	isElementInteractive = ( element: HTMLElement ): boolean => {
		if ( !element ) {
			return false
		}
		const nodeName = element.nodeName
		// check if element is focused
		if ( element === document.activeElement && element !== document.body && (!element.hasAttribute( 'tabindex' ) || element.tabIndex > -1 ) ) {
			return true
		}

		// check if element is focusable
		if ( element.hasAttribute( 'tabindex' ) && element.tabIndex > -1 ) {
			return true
		}

		if (
			[
				'BUTTON',
				'DETAILS',
				'EMBED',
				'IFRAME',
				'KEYGEN',
				'LABEL',
				'SELECT',
				'TEXTAREA',
				'AUDIO',
				'VIDEO',
				'INPUT',
			].includes( element.nodeName )
		) {
			// check if element is disabled
			if ( ( element as HTMLInputElement ).disabled ) {
				return false
			}
			return true;
		}
	
		if ( element.nodeName === 'A' && element.hasAttribute('href')) {
			return true;
		}
	
		if ( ( element instanceof HTMLInputElement ) && !( ( element as HTMLInputElement ).type === 'hidden' ) ) {
			return true;
		}
	
		if (
			[ 'AUDIO', 'VIDEO' ].includes(nodeName) &&
			element.hasAttribute('controls')
		) {
			return true;
		}
	
		if (
			[ 'IMG', 'OBJECT' ].includes(nodeName) &&
			element.hasAttribute('usemap')
		) {
			return true;
		}

		// check if element has a click event
		const click = element.getAttribute( 'onclick' )
		if ( click ) {
			return true
		}

		// check recursively the parent nodes
		if ( element.parentElement ) {
			return this.isElementInteractive( element.parentElement )
		}

		return false

	}


	clickWork = () => {
		const clicks = this.clickQueue.slice()
		this.clickQueue = []

		/**
		 * criteria:
		 * 1 - the pageX/pageY should be within 10px of the previous click
		 * 2 - the time difference should be less than 300ms
		 * 3 - should be a sequence of 3 or more clicks
		 */

		const rageRegions: ( typeof clicks )[] = []

		// eslint-disable-next-line no-constant-condition
		while( true ) {
			const item = clicks.shift()
			if ( !item ) {
				break
			}
			const { pgX, pgY } = item

			let regionFound = false
			for( const region of rageRegions ) {
				const last = region[ region.length - 1 ]
				if ( last ) {
					const { pgX: rgPgX, pgY: rgPgY, t } = last
					if ( Math.abs( pgX - rgPgX ) <= 10 && Math.abs( pgY - rgPgY ) <= 10 && item.t - t < 300 ) {
						region.push( item )
						regionFound = true
						break
					}
				}
			}
			if ( !regionFound ) {
				rageRegions.push( [ item ] )
			}
		}

		const acc: typeof clicks = []

		// debug: hostname = uxcam.com
		if ( location.hostname === 'uxcam.com' ) {
			console.log( 'rageRegions', rageRegions )
		}

		for( const region of rageRegions ) {
			const isRage = region.length > 2

			for( const item of region ) {
				item.isRage = isRage
				acc.push( item )
			}
		}

		// sort acc by seq
		acc.sort( ( a, b ) => a.s - b.s )

		for( const item of acc ) {
			if ( item.ignore ) { continue }
			const type = item.type === ChangeType.MOUSE_CLICK ? 'MOUSE_CLICK' : 'MOUSE_DOUBLE_CLICK'
			const typeMin = item.type

			const change: MouseClickChange | MouseClickChangeMin | MouseDoubleClickChange | MouseDoubleClickChangeMin = this.min ? {
				s: item.s,
				t: item.t,
				ct: typeMin,
				tg: item.target,
				a: item.activity,
				ires: item.isResponsive,
				x: item.x,
				y: item.y,
				rg: item.isRage,
				sel: item.selector,
			} : {
				seq: item.s,
				time: item.t,
				type: type,
				target: item.target,
				isResponsive: item.isResponsive,
				activity: item.activity,
				x: item.x,
				y: item.y,
				isRage: item.isRage,
				selector: item.selector,
			}

			this.__onChange( change )
		}

	}

	handleMouseClick = ( event: MouseEvent ) => {

		if ( !event.target || !this.__reverseDomMap.has( event.target as HTMLElement ) ) {
			return
		}

		const {
			x, y
		} = event

		const activity = new URL( this.getOccludedURL() ).pathname

		let target: string

		try {
			target = this.getUUIDByElement( event.target as HTMLElement )
		} catch ( e ) {
			this.registerElement( event.target as HTMLElement )

			return
		}

		this.clickQueue.push( {
			x, y,
			pgX: event.pageX,
			pgY: event.pageY,
			target,
			selector: this.getElementSelector( event.target as HTMLElement ),
			isResponsive: this.isElementInteractive( event.target as HTMLElement ),
			s: this.getSeq(),
			t: this.getTime(),
			activity,
			isRage: false,
			type: ChangeType.MOUSE_CLICK,
		} )

		this.scheduleClickWork()

		// if element in not interactive, send a dead click event
		if ( !this.isElementInteractive( event.target as HTMLElement ) ) {
			this.handleEvent( 'uxc_dead_click', {
				x, y,
				selector: this.getElementSelector( event.target as HTMLElement ),
			} )
		}
	}

	handleDoubleClick = ( event: MouseEvent ) => {

		// const selector = this.getElementSelector( event.target as HTMLElement )
		const {
			x, y
		} = event

		if ( !event.target || !this.__reverseDomMap.has( event.target as HTMLElement ) ) {
			return
		}

		const activity = new URL( this.getOccludedURL() ).pathname

		let target: string

		try {
			target = this.getUUIDByElement( event.target as HTMLElement )
		} catch ( e ) {
			return
		}

		this.clickQueue.push( {
			x, y,
			pgX: event.pageX,
			pgY: event.pageY,
			target,
			selector: this.getElementSelector( event.target as HTMLElement ),
			isResponsive: this.isElementInteractive( event.target as HTMLElement ),
			s: this.getSeq(),
			t: this.getTime(),
			activity,
			isRage: false,
			type: ChangeType.MOUSE_DOUBLE_CLICK,
		} )

		this.clickQueue.push( {
			x, y,
			pgX: event.pageX,
			pgY: event.pageY,
			target,
			selector: this.getElementSelector( event.target as HTMLElement ),
			isResponsive: this.isElementInteractive( event.target as HTMLElement ),
			s: this.getSeq(),
			t: this.getTime(),
			activity,
			isRage: false,
			type: ChangeType.MOUSE_DOUBLE_CLICK,
			ignore: true,
		} )

		this.scheduleClickWork()

		// if element in not interactive, send a dead click event
		if ( !this.isElementInteractive( event.target as HTMLElement ) ) {
			this.handleEvent( 'uxc_dead_click', {
				x, y,
				selector: this.getElementSelector( event.target as HTMLElement ),
			} )
		}

		// const change: MouseDoubleClickChange | MouseDoubleClickChangeMin = this.min ? {
		// 	s: this.getSeq(),
		// 	t: this.getTime(),
		// 	ct: ChangeType.MOUSE_DOUBLE_CLICK,
		// 	tg: target,
		// 	a: activity,
		// 	x,
		// 	y,
		// } : {
		// 	seq: this.getSeq(),
		// 	time: this.getTime(),
		// 	type: 'MOUSE_DOUBLE_CLICK',
		// 	target: target,
		// 	activity: activity,
		// 	x,
		// 	y,
		// }

		// this.__onChange( change )

		// this.handleEvent( 'double_click', {
		// 	x, y, selector,
		// } )

	}

	handleMouseOver = ( event: MouseEvent ) => {
		if ( !this.__reverseDomMap.has( event.target as HTMLElement ) ) {
			return
		}

		const change: MouseOverChange | MouseOverChangeMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.MOUSE_OVER,
			tg: this.getUUIDByElement( event.target as HTMLElement ),
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'MOUSE_OVER',
			target: this.getUUIDByElement( event.target as HTMLElement ),
		}

		this.__onChange( change )
	}

	handleMouseOut = ( event: MouseEvent ) => {
		if ( !this.__reverseDomMap.has( event.target as HTMLElement ) ) {
			return
		}
		const change: MouseOutChange | MouseOutChangeMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.MOUSE_OUT,
			tg: this.getUUIDByElement( event.target as HTMLElement ),
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'MOUSE_OUT',
			target: this.getUUIDByElement( event.target as HTMLElement ),
		}

		this.__onChange( change )
	}

	private touchMap: Map<number, {
		prevX: number,
		prevY: number,
		id: number,
		points: [ number, number ][],
		initScrollX: number,
		initScrollY: number,
		seq: number,
		time: number,
	} > = new Map()

	handleTouchStart = ( event: TouchEvent ) => {
		for ( const touch of event.changedTouches ) {
			const { identifier, clientX, clientY } = touch
			// get initial scroll position
			const {
				scrollX,
				scrollY,
			} = window

			this.touchMap.set( identifier, {
				prevX: clientX,
				prevY: clientY,
				id: identifier,
				points: [ [ clientX, clientY ] ],
				initScrollX: scrollX,
				initScrollY: scrollY,
				seq: this.getSeq(),
				time: this.getTime(),
			} )
		}
	}

	handleTouchMove = ( event: TouchEvent ) => {
		for ( const touch of event.changedTouches ) {
			const { identifier, clientX, clientY } = touch
			const touchData = this.touchMap.get( identifier )
			if ( !touchData ) {
				continue
			}

			const {
				initScrollX,
				initScrollY,
			} = touchData

			const {
				scrollX,
				scrollY,
			} = window

			const adjustedX = clientX + ( scrollX - initScrollX )
			const adjustedY = clientY + ( scrollY - initScrollY )

			const change = this.min ? {
				s: this.getSeq(),
				t: this.getTime(),
				ct: ChangeType.TOUCH,
				tg: this.getUUIDByElement( event.target as HTMLElement ),
				ox: touchData.prevX,
				oy: touchData.prevY,
				x: adjustedX,
				y: adjustedY,
			} as TouchChangeMin: {
				seq: this.getSeq(),
				time: this.getTime(),
				type: 'TOUCH_EXPERIMENTAL',
				target: this.getUUIDByElement( event.target as HTMLElement ),
				ox: touchData.prevX,
				oy: touchData.prevY,
				x: adjustedX,
				y: adjustedY,
			} as TouchChange

			touchData.prevX = clientX
			touchData.prevY = clientY
			touchData.initScrollX = scrollX
			touchData.initScrollY = scrollY
			const point = [
				clientX + window.scrollX,
				clientY + window.scrollY,
			] as [ number, number ]

			touchData.points.push( point )

			this.__onChange( change )
		}
	}

	handleTouchEnd = ( event: TouchEvent ) => {
		for ( const touch of event.changedTouches ) {
			const { identifier, clientX, clientY } = touch
			const touchData = this.touchMap.get( identifier )
			if ( !touchData ) {
				continue
			}

			const change = this.min ? {
				s: this.getSeq(),
				t: this.getTime(),
				ct: ChangeType.TOUCH,
				tg: this.getUUIDByElement( event.target as HTMLElement ),
				ox: touchData.prevX,
				oy: touchData.prevY,
				x: clientX,
				y: clientY,
			} as TouchChangeMin : {
				seq: this.getSeq(),
				time: this.getTime(),
				type: 'TOUCH_EXPERIMENTAL',
				target: this.getUUIDByElement( event.target as HTMLElement ),
				ox: touchData.prevX,
				oy: touchData.prevY,
				x: clientX,
				y: clientY,
			} as TouchChange

			this.__onChange( change )

			// collect all touch points
			const {
				points,
				seq,
				time,
			} = touchData
			if ( points.length > 1 ) {

				const x = points[0][0]
				const y = points[0][1]

				const isSwipe = IsSwipe( points )
				if ( isSwipe ) {
					const direction = SwipeDirection( points )
					this.__onChange( this.min ? {
						seq,
						time,
						type: 'GESTURE',
						gestureType: direction,
						x,
						y,
						points,
					} as Gesture : {
						s: seq,
						t: time,
						ct: ChangeType.GESTURE,
						gt: direction,
						d: direction,
						p: points,
						x,
						y,
					} as GestureMin )
				} else {
					this.__onChange( this.min ? {
						seq,
						time,
						type: 'GESTURE',
						gestureType: GestureType.TRAIL,
						x,
						y,
						points,
					} as Gesture : {
						s: seq,
						t: time,
						ct: ChangeType.GESTURE,
						gt: GestureType.TRAIL,
						x,
						y,
						p: points,
					} as GestureMin )
				}
			}

			this.touchMap.delete( identifier )
		}
	}

	handleWindowResize = () => {
		const change: WindowResize | WindowResizeMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.WINDOW_RESIZE,
			w: window.innerWidth,
			h: window.innerHeight,
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'WINDOW_RESIZE',
			width: window.innerWidth,
			height: window.innerHeight,
		}

		this.__onChange( change )
	}

	handleViewPortResize = ( e: Event ) => {
		const viewPort = e.target as VisualViewport
		const {
			width,
			height,
			scale,
			offsetLeft,
			offsetTop,
			pageLeft,
			pageTop,
		} = viewPort

		const change: ViewPortResize | ViewPortResizeMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.VIEWPORT_RESIZE_EXPERIMENTAL,
			w: width,
			h: height,
			sc: scale,
			ol: offsetLeft,
			ot: offsetTop,
			pl: pageLeft,
			pt: pageTop,
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'VIEWPORT_RESIZE_EXPERIMENTAL',
			width,
			height,
			scale,
			offsetLeft,
			offsetTop,
			pageLeft,
			pageTop,
		}

		this.__onChange( change )
	}

	handleSubmit = ( event: Event ) => {
		if ( !this.__reverseDomMap.has( event.target as HTMLElement ) ) {
			return
		}
		
		this.handleEvent( 'uxc_submit', {
			uxc_target: this.getElementSelector( event.target as HTMLElement ),
			uxc_page: new URL( this.getOccludedURL() ).pathname,
			internal: true,
		} )
	}


	public setUserProperty = ( key: string, value: unknown ) => {
		const change: UserProperty | UserPropertyMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.USER_PROPERTY,
			k: key,
			v: value,
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'USER_PROPERTY',
			key,
			value,
		}

		this.__onChange( change )
	}

	public setClientIP  = ( ip: string ) => {
		const change: ClientIp | ClientIpMin = this.min ? {
			s: this.getSeq(),
			t: this.getTime(),
			ct: ChangeType.CLIENT_IP,
			ip,
		} : {
			seq: this.getSeq(),
			time: this.getTime(),
			type: 'CLIENT_IP',
			ip,
		}

		this.__onChange( change )
	}

	private __mutObserver: MutationObserver | null = null
	startMutationObserver = () => {
		this.debug( 'Observer started!' )
		/**
		 * listen for changes to the DOM
		 */

		this.__mutObserver = new MutationObserver( this.onMutation )
		this.collectDOM()
		this.__mutObserver.observe( document.head, {
			childList: true,
			attributes: true,
			characterData: true,
			subtree: true,
		} )
		this.__mutObserver.observe( document.body, {
			childList: true,
			attributes: true,
			characterData: true,
			subtree: true,
		} )

		document.addEventListener( 'scroll', this.handleScroll, true )
		document.addEventListener( 'focus', this.handleFocus, true )
		document.addEventListener( 'mousemove', this.handleMouseMove, true )
		document.addEventListener( 'click', this.handleMouseClick, true )
		document.addEventListener( 'dblclick', this.handleDoubleClick, true )
		document.addEventListener( 'mouseenter', this.handleMouseOver, true )
		document.addEventListener( 'mouseleave', this.handleMouseOut, true )
		document.addEventListener( 'submit', this.handleSubmit, true )
		document.addEventListener( 'touchstart', this.handleTouchStart, true )
		document.addEventListener( 'touchend', this.handleTouchEnd, true )
		document.addEventListener( 'touchmove', this.handleTouchMove, true )
		// process clicks before unload
		window.addEventListener( 'beforeunload', this.clickWork, true )

		/**
		 * listen to window resize
		 */
		// check if VisualViewport is available
		if ( window.visualViewport ) {
			window.visualViewport.addEventListener( 'resize', this.handleViewPortResize )
		}
		window.addEventListener( 'resize', this.handleWindowResize )

		return () => {
			document.documentElement.style.border = 'none'
			if ( this.__mutObserver ) {
				this.__mutObserver.disconnect()
			}
			document.removeEventListener( 'scroll', this.handleScroll, true )
			document.removeEventListener( 'focus', this.handleFocus, true )
			document.removeEventListener( 'mousemove', this.handleMouseMove, true )
			document.removeEventListener( 'click', this.handleMouseClick, true )
			document.removeEventListener( 'dblclick', this.handleDoubleClick, true )
			document.removeEventListener( 'mouseenter', this.handleMouseOver, true )
			document.removeEventListener( 'mouseleave', this.handleMouseOut, true )
			document.removeEventListener( 'submit', this.handleSubmit, true )
			document.removeEventListener( 'touchstart', this.handleTouchStart, true )
			document.removeEventListener( 'touchend', this.handleTouchEnd, true )
			document.removeEventListener( 'touchmove', this.handleTouchMove, true )
			window.removeEventListener( 'beforeunload', this.clickWork, true )

			if ( window.visualViewport ) {
				window.visualViewport.removeEventListener( 'resize', this.handleViewPortResize )
			}
			window.removeEventListener( 'resize', this.handleWindowResize )

		}
	}

	private getOccludedURL = () => {
		const useUrl = this.occlusionOptions.url( window.location.toString() )
		const urlObj = new URL( useUrl )
		const search = new URLSearchParams( urlObj.search )

		for ( const occludedKey of this.occlusionOptions.queryParams ) {
			if ( search.has( occludedKey ) ) {
				search.set( occludedKey, '__occluded__' )
			}
		}
		const searchPart = search.toString()

		return `${urlObj.origin}${urlObj.pathname}${ searchPart ? '?' + searchPart : '' }${ urlObj.hash }`
	}

	private capturePageView = () => {
		const useUrl = this.getOccludedURL()
		const url = new URL( useUrl )

		this.handleEvent( 'uxc_url_navigate', {
			query: url.search,
			path: url.pathname,
			hash: url.hash,
			hostname: url.hostname,
		} )
	}

	private startUrlObserver = () => {

		const originalPushState = history.pushState
		const originalReplaceState = history.replaceState

		history.pushState = new Proxy( history.pushState, {
			apply: ( target, thisArg, args ) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const result = target.apply( thisArg, args as unknown as any )
				const useUrl = this.getOccludedURL()

				const change: URLChange | URLChangeMin = this.min ? {
					s: this.getSeq(),
					t: this.getTime(),
					ct: ChangeType.URL_CHANGE,
					u: useUrl,
					utm: collectUTM(),
				} : {
					seq: this.getSeq(),
					time: this.getTime(),
					type: 'URL_CHANGE',
					url: useUrl,
					utm: collectUTM(),
				}

				this.__onChange( change )
				this.capturePageView()
				return result
			},
		} )

		history.replaceState = new Proxy( history.replaceState, {
			apply: ( target, thisArg, args ) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const result = target.apply( thisArg, args as unknown as any )
				const useUrl = this.getOccludedURL()

				const change: URLChange | URLChangeMin = this.min ? {
					s: this.getSeq(),
					t: this.getTime(),
					ct: ChangeType.URL_CHANGE,
					u: useUrl,
					utm: collectUTM(),
				} : {
					seq: this.getSeq(),
					time: this.getTime(),
					type: 'URL_CHANGE',
					url: useUrl,
					utm: collectUTM(),
				}

				this.__onChange( change )
				this.capturePageView()
				return result
			},
		} )

		const handlePopState = () => {
			const useUrl = this.getOccludedURL()

			const change: URLChange | URLChangeMin = this.min ? {
				s: this.getSeq(),
				t: this.getTime(),
				ct: ChangeType.URL_CHANGE,
				u: useUrl,
				utm: collectUTM(),
			} : {
				seq: this.getSeq(),
				time: this.getTime(),
				type: 'URL_CHANGE',
				url: useUrl,
				utm: collectUTM(),
			}
			this.__onChange( change )
			this.capturePageView()
		}

		// attach window location change listener
		window.addEventListener( 'popstate', handlePopState )

		// clean up
		return () => {
			history.pushState = originalPushState
			history.replaceState = originalReplaceState
			window.removeEventListener( 'popstate', handlePopState )
		}

	}

	private startInputObserver = () => {

		const debounceMap: Map<HTMLElement, number> = new Map()
		// will be used for uxc_input event
		const lockMap: Map<HTMLElement, boolean> = new Map()

		const onInput = ( event: Event ) => {

			const eventTarget = event.target as HTMLInputElement | null
			if ( !eventTarget || !this.__reverseDomMap.has( eventTarget ) ) {
				return
			}
			/**
			 * check the input element is registered
			 */
			if ( !this.__reverseDomMap.has( eventTarget ) ) { return }

			const existingTimeout = debounceMap.get( event.target as HTMLElement )
			const isLocked = lockMap.get( event.target as HTMLElement )

			if ( existingTimeout ) {
				clearTimeout( existingTimeout )
			}
			let { value: useValue } = eventTarget
			// check if it's a checkbox or radio
			if ( eventTarget.type === 'checkbox' || eventTarget.type === 'radio' ) {
				const state = ( eventTarget as HTMLInputElement ).checked || false
				const change: CheckedChange | CheckedChangeMin = this.min ? {
					s: this.getSeq(),
					t: this.getTime(),
					ct: ChangeType.CHECKED_CHANGE,
					tg: this.getUUIDByElement( eventTarget ),
					c: state,
				} : {
					seq: this.getSeq(),
					time: this.getTime(),
					type: 'CHECKED_CHANGE',
					target: this.getUUIDByElement( eventTarget ),
					checked: state,
				}

				this.__onChange( change )
				return
			}

			if ( this.isSensitiveInput( eventTarget ) ) {
				// check if it's a numeric input
				if ( eventTarget.type === 'number' ) {
					useValue = '000000'
				} else {
					useValue = '******'
				}
			}

			const changeTimeout = setTimeout( () => {
				const change: InputChange | InputChangeMin = this.min ? {
					s: this.getSeq(),
					t: this.getTime(),
					ct: ChangeType.INPUT_CHANGE,
					tg: this.getUUIDByElement( eventTarget ),
					v: useValue,
				} : {
					seq: this.getSeq(),
					time: this.getTime(),
					type: 'INPUT_CHANGE',
					target: this.getUUIDByElement( eventTarget ),
					value: useValue,
				}
				this.__onChange( change )
				debounceMap.delete( eventTarget )
			}, 200 )

			if ( !isLocked ) {
				lockMap.set( event.target as HTMLElement, true )
				const handleBlur = () => {
					// send uxc_input event on blur
					const properties = {
						uxc_target: this.getElementSelector( eventTarget as HTMLElement ),
						uxc_page: new URL( this.getOccludedURL() ).pathname,
						internal: true,
					} as Record<string, unknown>

					// add name if available
					if ( eventTarget.name ) {
						properties["uxc_name"] = eventTarget.name
					}

					this.handleEvent( 'uxc_input', properties )
					lockMap.delete( eventTarget as HTMLElement )
				}
	
				eventTarget.addEventListener( 'blur', handleBlur, { once: true } )
			}

			debounceMap.set( eventTarget, changeTimeout as unknown as number )

		}

		document.addEventListener( 'input', onInput, true )
		document.addEventListener( 'change', onInput, true )

		return () => {
			document.removeEventListener( 'input', onInput, true )
			document.removeEventListener( 'change', onInput, true )
		}
	}

	private pageCollected = false

	collectDOM = () => {
		if ( this.pageCollected ) { return }
		this.pageCollected = true
		this.debug( 'start collecting page' )
		const documentNode = this.collectElement( document.documentElement as unknown as HTMLElement )
		this.debug( 'collected page' )
		const initTime = this.getTime()
		const initSeq = this.getSeq()

		const min = this.min
		const resizeChange: WindowResize | WindowResizeMin = min ? {
			s: initSeq,
			t: initTime,
			ct: ChangeType.WINDOW_RESIZE,
			w: window.innerWidth,
			h: window.innerHeight,
		} : {
			seq: initSeq,
			time: initTime,
			type: 'WINDOW_RESIZE',
			width: window.innerWidth,
			height: window.innerHeight,
		}

		this.__onChange( resizeChange )

		const url = this.getOccludedURL()

		const pageLoadChange: PageLoad | PageLoadMin = min ? {
			s: initSeq,
			t: initTime,
			ct: ChangeType.PAGE_LOAD,
			n: documentNode as INodeMin,
			u: url,
			utm: collectUTM(),
			ts: document.documentElement.scrollTop,
			ls: document.documentElement.scrollLeft,
		} : {
			seq: initSeq,
			time: initTime,
			type: 'PAGE_LOAD',
			node: documentNode as INode,
			url,
			utm: collectUTM(),
			topScroll: document.documentElement.scrollTop,
			leftScroll: document.documentElement.scrollLeft
		}

		this.__onChange( pageLoadChange )
		this.capturePageView()

		const domReadyChange: DomReady | DomReadyMin = min ? {
			s: initSeq,
			t: initTime,
			ct: ChangeType.DOM_READY,
		} : {
			seq: initSeq,
			time: initTime,
			type: 'DOM_READY',
		}

		this.scheduleOnChangeEvent( domReadyChange )
	}

}